import styled from 'styled-components';

export const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const Title = styled.h5`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  text-align: left;
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const Empty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

export const FileRow = styled.tr<{ $isEmpty?: boolean }>`
  cursor: pointer;
  background: ${({ $isEmpty }) => ($isEmpty ? 'rgba(255, 152, 0, 0.10)' : 'transparent')};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

export const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

export const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.danger}14;
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.status.danger};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Ячейка типа в режиме просмотра: бейдж сверху, под ним источник + «изменить». */
export const KindCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

/** Строка под бейджем: откуда взялся тип + кнопка правки. */
export const KindMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Откуда система взяла тип — «определено по имени файла» / «определил ИИ». */
export const SourceCaption = styled.span`
  font-size: 10px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Ненавязчивая «изменить» — коррекция авто-типа вторична, поэтому мелко. */
export const EditKindBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.muted};
  transition: color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Ячейка типа в режиме правки: select + кнопка отмены. */
export const KindEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/** Квадратная иконочная кнопка (отмена правки). */
export const CancelKindBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.text.secondary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

/** Компактный select типа документа прямо в строке таблицы. */
export const KindSelect = styled.select`
  font: inherit;
  font-size: 11px;
  padding: 2px 4px;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ScopeBanner = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  line-height: 1.5;
`;

export const SuccessBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.success}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.success};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  line-height: 1.5;
`;

export const ErrorBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.5;
`;

// ─── Styled Components replacing inline styles ─────────────────────────

export const ScopeErrorBanner = styled(ErrorBanner)`
  margin-top: 0;
  margin-bottom: 12px;
`;

export const ScopeSuccessNote = styled(Empty)`
  padding-top: 0;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const ThName = styled.th`
  width: 28%;
`;

export const ThKind = styled.th`
  width: 18%;
`;

export const ThSize = styled.th`
  width: 10%;
`;

export const ThExtracted = styled.th`
  width: 14%;
`;

export const ThUploaded = styled.th`
  width: 14%;
`;

export const ThAction = styled.th`
  width: 16%;
  text-align: right;
`;

export const ExtractedCharsCell = styled.td<{ $isEmpty?: boolean; $isAttached?: boolean }>`
  color: ${({ $isEmpty, $isAttached, theme }) =>
    $isEmpty
      ? theme.colors.status.warning
      : $isAttached
        ? theme.colors.text.muted
        : undefined};
  font-weight: ${({ $isEmpty }) => ($isEmpty ? 600 : 400)};
`;

export const TdRight = styled.td`
  text-align: right;
`;

export const AttachOnlyLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  font-size: 13px;
  line-height: 1.5;
  cursor: pointer;
`;

export const AttachOnlyCheckbox = styled.input`
  margin-top: 3px;
  flex-shrink: 0;
`;

export const AttachOnlyDescription = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const UploadModalActionRow = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
